<template>
  <div class="rich-editor" :class="{ 'rich-editor--disabled': disabled }">
    <!-- 工具条：加粗 / 斜体 / 下划线 / 有序列表 / 无序列表 / 分段 / 清除格式 -->
    <div class="rich-editor__bar" v-if="!disabled">
      <button
        type="button"
        class="rich-editor__btn"
        v-for="b in buttons"
        :key="b.cmd"
        :class="{ 'rich-editor__btn--on': isActive(b.cmd) }"
        :title="b.title"
        @mousedown.prevent="exec(b.cmd, b.value)"
      >
        <span v-html="b.text"></span>
      </button>
    </div>

    <!-- 编辑区：contenteditable，输入时同步 v-model -->
    <div
      ref="editor"
      class="rich-editor__body"
      :contenteditable="!disabled"
      :data-placeholder="placeholder"
      @input="onInput"
      @blur="onBlur"
      @paste="onPaste"
    ></div>

    <!-- 字数：与 textarea 备注体验一致 -->
    <div class="rich-editor__foot" v-if="showCount">
      <span>{{ plainLen }} 字</span>
    </div>
  </div>
</template>

<script>
/**
 * 自研富文本编辑器（不引第三方库）
 * ------------------------------------------------------------------
 * 需求（04）：备注富文本化。项目原先 main.js 已引入 vue-quill-editor，
 * 但 Quill 体积大、样式与 H5 端 Vant 风格冲突明显，且历史组件里未真正使用。
 * 这里用原生 contenteditable + document.execCommand 实现最小可用集：
 *   加粗 / 斜体 / 下划线 / 有序列表 / 无序列表 / 分段 / 清除格式
 *
 * 输出：HTML 字符串（直接写入 remark 字段，后端 plainText() 会剥标签做列表展示）
 * 契约：与 <textarea> 的 v-model 完全一致（支持 :value + @input）
 *
 * 安全：粘贴时强制转为纯文本，避免把外部富文本的字体/颜色/class/script 带进来，
 *       保证输出 HTML 只含白名单标签（b/strong/i/em/u/ul/ol/li/div/br/p）。
 */
const ALLOW_TAGS = ["B", "STRONG", "I", "EM", "U", "UL", "OL", "LI", "DIV", "BR", "P"];

export default {
  name: "RichEditor",
  props: {
    value: { type: String, default: "" },
    placeholder: { type: String, default: "请输入内容" },
    disabled: { type: Boolean, default: false },
    showCount: { type: Boolean, default: true }
  },
  data() {
    return {
      buttons: [
        { cmd: "bold", title: "加粗", text: "<b>B</b>" },
        { cmd: "italic", title: "斜体", text: "<i>I</i>" },
        { cmd: "underline", title: "下划线", text: "<u>U</u>" },
        { cmd: "insertUnorderedList", title: "无序列表", text: "•" },
        { cmd: "insertOrderedList", title: "有序列表", text: "1." },
        { cmd: "formatBlock", value: "div", title: "分段", text: "¶" },
        { cmd: "removeFormat", title: "清除格式", text: "⌫" }
      ],
      // 防止 v-model 回灌时触发 input 事件造成死循环
      _silent: false
    };
  },
  computed: {
    plainLen() {
      return this.stripTags(this.value || "").length;
    }
  },
  watch: {
    value(v) {
      this.syncFromValue(v);
    }
  },
  mounted() {
    this.syncFromValue(this.value);
    document.addEventListener("selectionchange", this.refreshActive);
  },
  beforeDestroy() {
    document.removeEventListener("selectionchange", this.refreshActive);
  },
  methods: {
    stripTags(html) {
      return String(html)
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();
    },
    // 外部 value 变化 -> 写入 DOM（内容一致时不重写，避免光标跳动）
    syncFromValue(v) {
      const el = this.$refs.editor;
      if (!el) return;
      // 空值统一按 <br> 处理，否则 contenteditable 高度塌陷无法输字
      const next = (v === null || v === undefined || v === "") ? "" : String(v);
      const cur = el.innerHTML || "";
      const curNorm = (cur === "<br>" || cur === "<div><br></div>") ? "" : cur;
      if (curNorm === next) return;
      this._silent = true;
      el.innerHTML = next;
      this._silent = false;
      this.refreshActive();
    },
    onInput() {
      if (this._silent) return;
      const el = this.$refs.editor;
      let html = el.innerHTML || "";
      // 全空时归一为 ""（避免提交一个 "<br>" 被后端当成有值）
      if (this.stripTags(html) === "" && !/<(ul|ol|li)/i.test(html)) html = "";
      this.$emit("input", html);
    },
    onBlur() {
      this.$emit("blur", this.value);
    },
    // 粘贴：强制纯文本，防外部样式/脚本注入
    onPaste(e) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text/plain") || "";
      if (!text) return;
      // 保留换行为段落：按行插入 div
      const lines = text.split(/\r?\n/);
      const html = lines
        .map(l => l.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"))
        .join("<br>");
      document.execCommand("insertHTML", false, html);
      this.onInput();
    },
    exec(cmd, val) {
      if (this.disabled) return;
      const el = this.$refs.editor;
      el.focus();
      try {
        document.execCommand(cmd, false, val === undefined ? null : val);
      } catch (e) {
        // 老 WebView 不支持某命令时静默忽略，不阻断输入
        console.log("[RichEditor] execCommand failed:", cmd, e);
      }
      this.cleanup();
      this.onInput();
      this.refreshActive();
    },
    // 清理 execCommand 可能产生的 font 标签 / 内联样式，收敛到白名单
    cleanup() {
      const el = this.$refs.editor;
      if (!el) return;
      const walk = node => {
        const kids = Array.prototype.slice.call(node.childNodes);
        kids.forEach(n => {
          if (n.nodeType === 1) {
            if (n.tagName === "FONT") {
              // font -> 保留其内容，必要时转成 b/i/u
              const frag = document.createDocumentFragment();
              while (n.firstChild) frag.appendChild(n.firstChild);
              n.parentNode.replaceChild(frag, n);
            } else if (!ALLOW_TAGS.includes(n.tagName)) {
              const frag = document.createDocumentFragment();
              while (n.firstChild) frag.appendChild(n.firstChild);
              n.parentNode.replaceChild(frag, n);
            } else {
              // 去掉所有 style/class/id 与事件属性
              n.removeAttribute("style");
              n.removeAttribute("class");
              n.removeAttribute("id");
              Array.prototype.slice.call(n.attributes || []).forEach(a => {
                if (/^on/i.test(a.name)) n.removeAttribute(a.name);
              });
              walk(n);
            }
          }
        });
      };
      walk(el);
    },
    isActive(cmd) {
      if (this.disabled) return false;
      try {
        return document.queryCommandState(cmd);
      } catch (e) {
        return false;
      }
    },
    refreshActive() {
      // 强制刷新工具条激活态
      this.$forceUpdate();
    }
  }
};
</script>

<style>
/* 富文本编辑器：与「备注」textarea 同一套外观（#f7f8fa 底、8px 圆角） */
.rich-editor {
  border: 1px solid #ebedf0;
  border-radius: 8px;
  background: #f7f8fa;
  overflow: hidden;
}
.rich-editor--disabled {
  opacity: 0.75;
}
.rich-editor__bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  background: #fff;
  border-bottom: 1px solid #ebedf0;
  flex-wrap: wrap;
}
.rich-editor__btn {
  min-width: 30px;
  height: 28px;
  padding: 0 7px;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  background: #fff;
  color: #323233;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.rich-editor__btn:active {
  background: #f2f3f5;
}
.rich-editor__btn--on {
  background: #eaf0ff;
  border-color: #2b5fd9;
  color: #2b5fd9;
}
.rich-editor__body {
  min-height: 74px;
  max-height: 240px;
  overflow-y: auto;
  padding: 10px;
  background: #f7f8fa;
  font-size: 14px;
  line-height: 1.65;
  color: #323233;
  outline: none;
  word-break: break-all;
  -webkit-user-select: text;
  user-select: text;
}
.rich-editor__body:empty::before,
.rich-editor__body[data-empty="1"]::before {
  content: attr(data-placeholder);
  color: #c8c9cc;
}
.rich-editor__body ul,
.rich-editor__body ol {
  margin: 4px 0;
  padding-left: 20px;
}
.rich-editor__foot {
  padding: 4px 10px;
  text-align: right;
  font-size: 12px;
  color: #969799;
  background: #fff;
  border-top: 1px solid #f2f3f5;
}
</style>



// WEBPACK FOOTER //
// src/components/RichEditor.vue